/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/scheduleModern/DayDetailView.tsx
 * PURPOSE: Màn "Lịch làm việc" — nội dung chế độ NGÀY (day view) đầy đủ
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Workforce Management — lịch ca của nhân viên, Vietnamese F&B
 * - Impact: Màn nhân viên xem hằng ngày (ca + chấm công thực tế của 1 ngày)
 * - Stakeholders: Nhân viên ca, Quản lý chi nhánh
 *
 * @technical-architecture
 * - Layer: Presentation (feature component của trang ScheduleModern)
 * - Pattern: Presentational component + TanStack Query (qua useSelfAttendanceHistoryRange)
 * - Dependencies: react-router-dom, @/hooks/useManagerProfile
 *
 * @cross-references
 * - Trang: frontend/src/pages/ScheduleModern.tsx (chế độ 'day')
 * - Hook lịch: frontend/src/features/scheduleModern/useScheduleModern.ts
 * - Hook chấm công: frontend/src/hooks/useManagerProfile.ts (useSelfAttendanceHistoryRange)
 * - Backend: backend/app/api/v1/schedule.py get_my_schedule()
 *            backend/app/api/v2/endpoints/attendance.py get_attendance_history()
 *
 * ⚠️ GHI CHÚ CHỦ ĐÍCH LỆCH SO VỚI MOBILE (quyết định của product owner 2026-09-04)
 * -----------------------------------------------------------------------------
 * Mobile KHÔNG có chế độ Ngày: `mobile/src/config/schedule.config.ts:221`
 * (`export type ViewMode = 'week' | 'month'`), chi tiết một ngày ở mobile đạt
 * được bằng cách bấm vào ô ngày trong WeekGrid (`onDayPress` → WeekShiftList,
 * `mobile/src/screens/schedule/ScheduleScreen.tsx:281-282`). Web có thêm nút
 * "Ngày" — trước đây chỉ hiển thị đúng danh sách ca giống hệt panel chi tiết
 * bấm-ngày, nên trông trống trải và gây khó hiểu ("chọn Ngày mà chẳng thấy gì
 * hơn").
 *
 * Quyết định: GIỮ chế độ Ngày, nhưng làm nội dung ĐẦY ĐẶN để nó có lý do tồn
 * tại riêng — không lặp lại panel bấm-ngày. Ngày = "trang chi tiết một ngày":
 *   [1] Tiêu đề ngày + nhãn Hôm nay / Đã qua / Sắp tới
 *   [2] 3 ô tổng hợp: số ca · giờ theo lịch · giờ làm thực tế
 *   [3] Dòng thời gian 24h (thấy được ca gãy — split shift FR-SS-001) + chi
 *       tiết từng ca (mã ca, giờ, thời lượng, chi nhánh, vị trí)
 *   [4] Chấm công thực tế của CHÍNH ngày đó (giờ vào/ra, trễ/về sớm, khoảng
 *       cách GPS, nhãn trạng thái) — dữ liệu thật từ API lịch sử chấm công
 *   [5] Hành động: "Chấm công ngay" (chỉ hôm nay) · "Xem lịch sử chấm công"
 *
 * Rule 3 (Zero-Mock): mọi con số ở đây suy ra từ trường THẬT của API. Không có
 * số liệu giả, không placeholder. Trường nào backend không trả thì khối đó
 * không render (xem các nhánh `== null` bên dưới).
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Shift } from './useScheduleModern';
import { useSelfAttendanceHistoryRange } from '@/hooks/useManagerProfile';
import type { SelfAttendanceRecord } from '@/services/api/managerProfileApi';
import styles from '@/pages/ScheduleModern.module.css';

const DAY_LABELS_FULL = [
  'Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy',
];

/** "YYYY-MM-DD" → Date LOCAL (tránh `new Date(iso)` vốn hiểu là UTC midnight). */
function fromLocalISO(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** "HH:MM" / "HH:MM:SS" → số phút từ 00:00. null nếu không parse được. */
function hhmmToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  return h * 60 + mm;
}

/** 510 → "8h30", 480 → "8h", 45 → "45 phút". */
function formatDuration(mins: number): string {
  if (mins < 60) return `${mins} phút`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}

/**
 * Thời lượng ca theo phút. Ca qua đêm (end < start, vd 22:00–06:00) được cộng
 * 24h — đúng như cách backend tính ca đêm, KHÔNG trả số âm.
 */
function shiftDurationMinutes(s: Shift): number | null {
  const start = hhmmToMinutes(s.start_time);
  const end = hhmmToMinutes(s.end_time);
  if (start == null || end == null) return null;
  return end >= start ? end - start : end + 24 * 60 - start;
}

/** Giờ "HH:MM" từ ISO datetime của bản ghi chấm công (theo giờ máy người dùng). */
function formatClockTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Ngày local "YYYY-MM-DD" của một ISO datetime. */
function localDateOf(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Câu hiển thị khi ngày đang xem KHÔNG có bản ghi chấm công nào.
 *
 * 2026-09-04 (người dùng phản hồi): bản đầu chỉ xét quá khứ/hôm nay/tương lai
 * nên ngày NGHỈ vẫn bị nói "Hôm nay bạn chưa chấm công." — đọc như đang nhắc
 * người ta thiếu sót trong khi họ không có ca nào để chấm. "Không có bản ghi"
 * mang ý nghĩa TRÁI NGƯỢC nhau tuỳ ngày đó có được phân ca hay không:
 *   - có ca mà không có bản ghi → BẤT THƯỜNG, đáng để người dùng để ý
 *   - không có ca            → BÌNH THƯỜNG, không việc gì phải nhắc
 * nên câu chữ phải xét cả hai trục (thời điểm × có ca), không chỉ thời điểm.
 */
export function emptyAttendanceCopy(args: {
  isPast: boolean;
  isToday: boolean;
  hasShift: boolean;
}): string {
  const { isPast, isToday, hasShift } = args;
  if (isToday) {
    return hasShift
      ? 'Hôm nay bạn chưa chấm công.'
      : 'Hôm nay bạn không có ca nên không cần chấm công.';
  }
  if (isPast) {
    return hasShift
      ? 'Ngày này có ca nhưng không có bản ghi chấm công.'
      : 'Ngày nghỉ — không có bản ghi chấm công.';
  }
  return hasShift
    ? 'Ngày này chưa tới — bạn sẽ chấm công khi vào ca.'
    : 'Ngày này chưa được phân ca.';
}

interface DayDetailViewProps {
  /** Ngày đang xem, "YYYY-MM-DD" (local). */
  dayIso: string;
  /** Hôm nay, "YYYY-MM-DD" (local) — do trang truyền xuống để test ổn định. */
  todayIso: string;
  /** Ca của ĐÚNG ngày này (trang đã gom sẵn theo ngày). */
  shifts: Shift[];
}

export const DayDetailView: React.FC<DayDetailViewProps> = ({ dayIso, todayIso, shifts }) => {
  const navigate = useNavigate();
  const date = useMemo(() => fromLocalISO(dayIso), [dayIso]);
  const isToday = dayIso === todayIso;
  const isPast = dayIso < todayIso;

  // Chỉ hỏi ĐÚNG một ngày (start = end = dayIso) — không kéo cả tuần về rồi lọc.
  const historyQuery = useSelfAttendanceHistoryRange(dayIso, dayIso);

  // Backend đã lọc theo khoảng ngày, nhưng vẫn lọc lại phía FE theo NGÀY LOCAL
  // của `clock_in`: khoảng ngày phía server tính theo timezone server, ca đêm
  // sát nửa đêm có thể rơi lệch một ngày.
  const records: SelfAttendanceRecord[] = useMemo(() => {
    const all = historyQuery.data?.records ?? [];
    return all.filter(r => localDateOf(r.clock_in) === dayIso);
  }, [historyQuery.data, dayIso]);

  const scheduledMinutes = useMemo(
    () => shifts.reduce((sum, s) => sum + (shiftDurationMinutes(s) ?? 0), 0),
    [shifts]
  );

  // Giờ làm THỰC TẾ: chỉ cộng bản ghi đã có clock_out (đang trong ca thì chưa
  // có tổng — không suy đoán bằng "giờ hiện tại trừ giờ vào").
  const actualMinutes = useMemo(() => {
    let total = 0;
    let counted = 0;
    for (const r of records) {
      if (!r.clock_out) continue;
      const inMs = new Date(r.clock_in).getTime();
      const outMs = new Date(r.clock_out).getTime();
      if (Number.isNaN(inMs) || Number.isNaN(outMs) || outMs <= inMs) continue;
      total += Math.round((outMs - inMs) / 60000);
      counted += 1;
    }
    return counted > 0 ? total : null;
  }, [records]);

  const dayBadge = isToday ? 'Hôm nay' : isPast ? 'Đã qua' : 'Sắp tới';
  const dayBadgeClass = isToday
    ? `${styles.dayBadge} ${styles.dayBadgeToday}`
    : isPast
      ? `${styles.dayBadge} ${styles.dayBadgePast}`
      : `${styles.dayBadge} ${styles.dayBadgeFuture}`;

  return (
    <div className={styles.dayView} data-testid="schedule-day-view">
      {/* [1] Tiêu đề ngày */}
      <div className={styles.dayViewHeader}>
        <div>
          <div className={styles.dayViewWeekday}>{DAY_LABELS_FULL[date.getDay()]}</div>
          <div className={styles.dayViewDate} data-testid="schedule-day-date">
            {String(date.getDate()).padStart(2, '0')}/{String(date.getMonth() + 1).padStart(2, '0')}/{date.getFullYear()}
          </div>
        </div>
        <span className={dayBadgeClass} data-testid="schedule-day-badge">{dayBadge}</span>
      </div>

      {/* [2] Ba ô tổng hợp */}
      <div className={styles.dayStats} data-testid="schedule-day-stats">
        <div className={styles.dayStat}>
          <span className={styles.dayStatValue}>{shifts.length}</span>
          <span className={styles.dayStatLabel}>ca làm</span>
        </div>
        <div className={styles.dayStat}>
          <span className={styles.dayStatValue}>
            {scheduledMinutes > 0 ? formatDuration(scheduledMinutes) : '—'}
          </span>
          <span className={styles.dayStatLabel}>theo lịch</span>
        </div>
        <div className={styles.dayStat}>
          <span className={styles.dayStatValue} data-testid="schedule-day-actual">
            {actualMinutes != null ? formatDuration(actualMinutes) : '—'}
          </span>
          <span className={styles.dayStatLabel}>thực tế</span>
        </div>
      </div>

      {/* [3] Dòng thời gian 24h + chi tiết ca */}
      <section className={styles.daySection}>
        {/* Ngày nghỉ thì không có dòng thời gian nào để xem — đổi tiêu đề cho
            khỏi mâu thuẫn với nội dung bên dưới. */}
        <h4 className={styles.daySectionTitle}>
          {shifts.length > 0 ? 'Dòng thời gian trong ngày' : 'Ca làm việc'}
        </h4>
        {shifts.length === 0 ? (
          <div className={styles.dayRestCard} data-testid="schedule-day-rest">
            <div className={styles.dayRestTitle}>Ngày nghỉ — không có ca theo lịch</div>
            <p className={styles.dayRestText}>
              Ngày này bạn không được phân ca. Nếu bạn nghĩ đây là nhầm lẫn, hãy liên hệ quản lý ca.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.dayTimeline} data-testid="schedule-day-timeline">
              <div className={styles.dayTimelineTrack}>
                {shifts.map(s => {
                  const start = hhmmToMinutes(s.start_time);
                  const dur = shiftDurationMinutes(s);
                  if (start == null || dur == null) return null;
                  // Ca qua nửa đêm chỉ vẽ phần nằm trong ngày này (phần còn lại
                  // thuộc ngày hôm sau, sẽ hiện khi xem ngày đó).
                  const width = Math.min(dur, 24 * 60 - start);
                  return (
                    <div
                      key={s.id}
                      className={styles.dayTimelineBar}
                      style={{ left: `${(start / (24 * 60)) * 100}%`, width: `${(width / (24 * 60)) * 100}%` }}
                      title={`${s.title} ${s.start_time}–${s.end_time}`}
                      data-testid={`schedule-day-timeline-bar-${s.id}`}
                    />
                  );
                })}
              </div>
              <div className={styles.dayTimelineTicks} aria-hidden="true">
                <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>24h</span>
              </div>
            </div>

            {shifts.map(s => {
              const dur = shiftDurationMinutes(s);
              return (
                <div key={s.id} className={styles.dayShiftCard} data-testid={`schedule-day-shift-${s.id}`}>
                  <div className={styles.dayShiftTime}>
                    {s.start_time}–{s.end_time}
                    {dur != null && <span className={styles.dayShiftDuration}> · {formatDuration(dur)}</span>}
                  </div>
                  <div className={styles.dayShiftTitle}>
                    {s.shift_code ? `Mã ca ${s.shift_code} · ` : ''}{s.title}
                  </div>
                  {(s.branch_name || s.position) && (
                    <div className={styles.dayShiftMeta}>
                      {s.branch_name ? `Chi nhánh: ${s.branch_name}` : ''}
                      {s.branch_name && s.position ? ' · ' : ''}
                      {s.position ? `Vị trí: ${s.position}` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </section>

      {/* [4] Chấm công thực tế của chính ngày đó */}
      <section className={styles.daySection}>
        <h4 className={styles.daySectionTitle}>Chấm công thực tế</h4>

        {historyQuery.isLoading && (
          <p className={styles.dayMutedText} role="status">Đang tải bản ghi chấm công…</p>
        )}

        {historyQuery.isError && (
          <p className={styles.dayMutedText} data-testid="schedule-day-attendance-error">
            Không tải được bản ghi chấm công của ngày này.
          </p>
        )}

        {!historyQuery.isLoading && !historyQuery.isError && records.length === 0 && (
          <p className={styles.dayMutedText} data-testid="schedule-day-attendance-empty">
            {emptyAttendanceCopy({ isPast, isToday, hasShift: shifts.length > 0 })}
          </p>
        )}

        {records.map(r => {
          const inTime = formatClockTime(r.clock_in);
          const outTime = formatClockTime(r.clock_out);
          return (
            <div key={r.id} className={styles.dayAttendanceCard} data-testid={`schedule-day-attendance-${r.id}`}>
              <div className={styles.dayAttendanceRow}>
                <span className={styles.dayAttendanceLabel}>Giờ vào</span>
                <span className={styles.dayAttendanceValue}>
                  {inTime ?? '—'}
                  {r.is_late && r.late_minutes > 0 && (
                    <span className={styles.dayAttendanceWarn}> · trễ {r.late_minutes} phút</span>
                  )}
                </span>
              </div>
              <div className={styles.dayAttendanceRow}>
                <span className={styles.dayAttendanceLabel}>Giờ ra</span>
                <span className={styles.dayAttendanceValue}>
                  {outTime ?? 'Chưa chấm công ra'}
                  {r.is_early_leave && r.early_leave_minutes > 0 && (
                    <span className={styles.dayAttendanceWarn}> · về sớm {r.early_leave_minutes} phút</span>
                  )}
                </span>
              </div>
              {r.clock_in_gps_distance_m != null && (
                <div className={styles.dayAttendanceRow}>
                  <span className={styles.dayAttendanceLabel}>Vị trí chấm vào</span>
                  <span className={styles.dayAttendanceValue}>
                    Cách chi nhánh {Math.round(r.clock_in_gps_distance_m)}m
                    {r.clock_in_within_geofence === false && (
                      <span className={styles.dayAttendanceWarn}> · ngoài phạm vi</span>
                    )}
                  </span>
                </div>
              )}
              {r.status_labels_vi?.length > 0 && (
                <div className={styles.dayAttendancePills}>
                  {r.status_labels_vi.map(label => (
                    <span key={label} className={styles.dayAttendancePill}>{label}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* [5] Hành động */}
      <div className={styles.dayActions}>
        {isToday && (
          <button
            type="button"
            className={styles.dayActionPrimary}
            onClick={() => navigate('/attendance')}
            data-testid="schedule-day-action-clock"
          >
            Chấm công ngay
          </button>
        )}
        <button
          type="button"
          className={styles.dayActionSecondary}
          onClick={() => navigate('/attendance/history')}
          data-testid="schedule-day-action-history"
        >
          Lịch sử chấm công
        </button>
      </div>
    </div>
  );
};

export default DayDetailView;
